<template>
    <div :class="{ header: true, 'header--master-doors': isMasterDoors, scrolled: isScrolled }">
        <!-- DESKTOP -->
        <template>
            <div class="header__container --desktop container">
                <!-- LEFT -->
                <div class="header__left">
                    <NuxtLink :to="localePath('/')" class="header__left__logo">
                        <img v-if="logo" :src="logo" alt="logo" />
                    </NuxtLink>

                    <button
                        :class="{ 'header__left__catalog-btn': true, active: isCatalogWindowActive }"
                        type="button"
                        @click="isCatalogWindowActive ? setActiveWindow(null) : setActiveWindow('catalog')"
                    >
                        <svg><use xlink:href="@/themes/novaporta/assets/icons/sprite.svg#catalogOpenIcon"></use></svg>
                        <span>{{ $t('globalTextAttributeCatalog') }}</span>
                    </button>

                    <hr />
                </div>

                <!-- CENTER -->
                <div class="header__center">
                    <SearchBar v-model="searchValue" @focus="toggleSearchWindowByValue(searchValue)" />
                    <SearchWindow
                        :isOpen="isSearchWindowActive"
                        :isLoading="isPreloaderSearchLoading"
                        :products="searchResultProducts"
                        :categories="searchResultCategories"
                    />
                </div>

                <!-- RIGHT -->
                <div class="header__right">
                    <hr />

                    <a class="header__right__phone" :href="`tel:${mainPhoneNumber}`">{{ mainPhoneNumber }}</a>
                    <div class="header__right__btns">
                        <NuxtLink :to="localePath('/contacts')" class="header__right__btns__item" type="button">
                            <svg><use xlink:href="@/themes/novaporta/assets/icons/sprite.svg#locationIcon"></use></svg>
                        </NuxtLink>
                        <NuxtLink :to="localePath('/favorites')" class="header__right__btns__item" type="button">
                            <svg><use xlink:href="@/themes/novaporta/assets/icons/sprite.svg#likeIcon"></use></svg>
                        </NuxtLink>
                        <NuxtLink :to="localePath('/cart')" class="header__right__btns__item" type="button">
                            <svg><use xlink:href="@/themes/novaporta/assets/icons/sprite.svg#cartIcon"></use></svg>
                        </NuxtLink>
                    </div>
                </div>
            </div>

            <!-- CATALOG WINDOW -->
            <CatalogWindow :isOpen="isCatalogWindowActive" :categories="parentCategories" />

            <!-- BG -->
            <div v-if="isBackgroundActive" class="header__background" @click="setActiveWindow(null)"></div>
        </template>
        <!-- MOBILE -->
        <div class="header__container --mobile container">
            <div class="header__left">
                <NuxtLink :to="localePath('/')" class="header__left__logo">
                    <img v-if="logo" :src="logo" alt="logo" />
                </NuxtLink>
            </div>

            <!-- LANGUAGE PANEL -->
            <div v-if="isLanguagePanelActive" class="header__language-panel">
                <div class="header__language-panel__langs">
                    <NuxtLink
                        prefetch
                        v-for="locale in $i18n.locales"
                        :key="locale.code"
                        :to="switchLocalePath(locale.code)"
                        :class="{
                            'header__language-panel__langs__item': true,
                            current: locale.code === $i18n.locale
                        }"
                    >
                        {{ locale.name }}
                    </NuxtLink>
                </div>
            </div>

            <!-- RIGHT -->
            <div class="header__right">
                <div class="header__right__btns">
                    <NuxtLink :to="localePath('/contacts')" class="header__right__btns__item" type="button">
                        <svg><use xlink:href="@/themes/novaporta/assets/icons/sprite.svg#locationIcon"></use></svg>
                    </NuxtLink>
                    <NuxtLink :to="localePath('/favorites')" class="header__right__btns__item" type="button">
                        <svg><use xlink:href="@/themes/novaporta/assets/icons/sprite.svg#likeIcon"></use></svg>
                    </NuxtLink>
                    <button
                        @click="setIsLanguagePanelActive(!isLanguagePanelActive)"
                        class="header__right__btns__item"
                        type="button"
                    >
                        <svg><use xlink:href="@/themes/novaporta/assets/icons/sprite.svg#globeIcon"></use></svg>
                    </button>
                    <button @click="openMobileMenu" class="header__right__btns__item" type="button">
                        <svg><use xlink:href="@/themes/novaporta/assets/icons/sprite.svg#menuIcon"></use></svg>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { createProductAgent } from '@/themes/novaporta/components/reusable/ProductAgent/types';
import { createCategoryLink, createParentCategoryItem } from '~/themes/novaporta/types/categories';
import { mapGetters } from 'vuex';
import viewport from '~/mixins/viewport';

const windowNames = ['catalog', 'search'];

export default {
    name: 'Header',
    mixins: [viewport],
    data() {
        return {
            isBackgroundActive: false,
            isLanguagePanelActive: false,
            isPreloaderSearchLoading: false,

            searchValue: '',
            activeWindow: null,

            isScrolled: false,

            searchResultCategories: [],
            searchResultProducts: [null, null, null] // Null item's have been used to shows skeletons preloaders of product component at first
        };
    },
    components: {
        SearchBar: () => import('./components/SearchBar'),
        SearchWindow: () => import('./components/SearchWindow'),
        CatalogWindow: () => import('./components/CatalogWindow')
    },
    computed: {
        ...mapGetters({
            globalSettings: 'global/globalSettings',
            mainPhoneNumber: 'global/mainPhoneNumber',
            categories: 'categories/getCategories',
            isMasterDoors: 'global/getIsMasterDoors'
        }),

        parentCategories() {
            return this.categories.map(c => {
                return createParentCategoryItem({
                    name: c.categoryTitle,
                    route: `/${c.categoryUrl}`,
                    img: c.categoryIcon
                });
            });
        },

        logo() {
            if (this.isUnderOrEqualViewport768) {
                return (
                    this.globalSettings?.logo?.mobileLogoLight ||
                    this.globalSettings?.logo?.mobileLogoDark ||
                    this.globalSettings?.logo?.logoDark ||
                    this.globalSettings?.logo?.logoDark
                );
            }
            return this.globalSettings?.logo?.logoDark || this.globalSettings?.logo?.logoDark;
        },

        isSearchValueFilled() {
            return this.searchValue.trim() ? true : false;
        },
        isSearchWindowActive() {
            return this.activeWindow === 'search' ? true : false;
        },
        isCatalogWindowActive() {
            return this.activeWindow === 'catalog' ? true : false;
        }
    },
    watch: {
        searchValue(newVal, oldVal) {
            const currentValue = newVal;

            if (newVal.trim() && oldVal.trim()) this.isPreloaderSearchLoading = true;

            this.toggleSearchWindowByValue(newVal),
                setTimeout(() => {
                    if (currentValue === this.searchValue) this.fetchProductsOnSearch(newVal);
                }, 500);
        },
        '$i18n.locale': {
            handler(e) {
                setTimeout(() => {
                    if (this.searchValue) {
                        this.fetchProductsOnSearch(this.searchValue);
                    }
                }, 500);
            }
        },
        $route: function (newVal, oldVal) {
            this.setIsBackgroundActive(null);
            this.setActiveWindow(null);
            this.setIsLanguagePanelActive(false);
        }
    },
    methods: {
        setIsBackgroundActive(flag) {
            this.isBackgroundActive = flag;
            // if (flag === false) this.setActiveWindow(null) // reset active window
        },
        setActiveWindow(name) {
            if (windowNames.includes(name)) {
                this.activeWindow = name;
                this.setIsBackgroundActive(true);
            } else if (name === null) {
                this.activeWindow = null;
                this.setIsBackgroundActive(false);
            } else {
                throw new Error(`Unvalid name of window, require one of [${windowNames.join(', ')}]`);
            }
        },
        setIsLanguagePanelActive(flag) {
            this.isLanguagePanelActive = flag;
        },

        async fetchProductsOnSearch(query) {
            try {
                this.isPreloaderSearchLoading = true;
                const { data } = await this.$api.search.getSearchResults(query);

                const newProducts = data.products.map(p => {
                    return createProductAgent({
                        active: p.active,
                        available: p.available,
                        id: p.productId,
                        name: p.title,
                        price: Number(p.price),
                        slug: p.url,
                        img: p.img,
                        oldPrice: p.old_price,
                        badges: p.badges_products, // TO DO: Require field badges
                        slides: [], // TO DO: Require slides field in product model
                        isOnSale: p.old_price ? true : false
                    });
                });

                this.searchResultCategories = data.resultsByCategory.map(c => {
                    return createCategoryLink({
                        name: c.categoryTitle,
                        route: `/${c.categoryUrl}`
                    });
                });

                this.searchResultProducts = newProducts;
            } catch (error) {
                throw error('Unvalid answer of search by query end-point');
            } finally {
                this.isPreloaderSearchLoading = false;
            }
        },

        openMobileMenu() {
            this.$store.dispatch('mobile-menu/setIsOpen', true);
        },
        toggleSearchWindowByValue(value) {
            if (value.trim()) this.setActiveWindow('search');
            else this.setActiveWindow(null);
        },
        handleScroll() {
            this.isScrolled = window.scrollY > 70;
        }
    },
    mounted() {
        if (this.isMasterDoors) {
            window.addEventListener('scroll', this.handleScroll);
        }
    },
    beforeDestroy() {
        if (this.isMasterDoors) {
            window.removeEventListener('scroll', this.handleScroll);
        }
    }
};
</script>

<style lang="scss" scoped>
@import './styles';
</style>
